import React, { createContext, useContext, useState, useCallback } from 'react';
import { fetchBuyCostsCategories } from '@/api/buy-cost';
import { useSearch } from "wouter"; // Adjust this import based on your actual API function

interface BuyCostContextData {
    userCategories: string[];
    setUserCategories: (categories: string[]) => void;
    isFetchingCategories: boolean;
    fetchCategories: () => Promise<void>;
    error: string | null;
}

const BuyCostContext = createContext<BuyCostContextData | undefined>(undefined);

export const useBuyCostContext = () => {
    const context = useContext(BuyCostContext);
    if (context === undefined) {
        throw new Error('useBuyCostContext must be used within a BuyCostContextProvider');
    }
    return context;
};

export const BuyCostContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [userCategories, setUserCategories] = useState<string[]>([]);
    const [isFetchingCategories, setIsFetchingCategories] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const searchParams = new URLSearchParams(useSearch());
    const shouldLaunchVariableBuyCosts: string = "true";

    const fetchCategories = useCallback(async () => {
        if (!shouldLaunchVariableBuyCosts) {
            setUserCategories([]);
            return;
        }
        setIsFetchingCategories(true);
        setError(null);
        try {
            const response = await fetchBuyCostsCategories();
            setUserCategories(response.userCategories);
        } catch (err) {
            console.error('Error fetching user categories:', err);
            setError('Failed to fetch user buyCost categories. Please try again.');
        } finally {
            setIsFetchingCategories(false);
        }
    }, []);

    return (
        <BuyCostContext.Provider
            value={{
                userCategories,
                setUserCategories,
                isFetchingCategories,
                fetchCategories,
                error,
            }}
        >
            {children}
        </BuyCostContext.Provider>
    );
};